import { theme } from '@features/executive-report/domain/theme';
import { TextProps, parseContent } from '@features/powerpoint';
import { ValueInput, formatters } from '@utils/formattersV2';
import { RslReport } from '@features/campaigns/subdomains/rsl';

const DRIVER_OFFSET = 1.36;

export type DriverTextsOptions = {
  index: number;
  tagline: TextProps['text'];
  value: ValueInput;
  descriptionText: TextProps['text'];
  metricName: string;
  exposedText: string | null;
  controlText: string | null;
};

export const driverTexts = (options: DriverTextsOptions): TextProps[] => [
  {
    text: options.tagline,
    options: {
      fontSize: 12,
      color: theme.colors.white,
      h: 0.3,
      w: 1.88,
      x: 8.65,
      y: 3.12 + DRIVER_OFFSET * options.index,
    },
  },
  {
    text: formatters.percentage(options.value, { decimals: 2 }),
    options: {
      fontSize: 28,
      color: theme.colors.white,
      h: 0.57,
      w: 1.81,
      x: 8.88,
      y: 3.36 + DRIVER_OFFSET * options.index,
    },
  },
  {
    text: options.descriptionText,
    options: {
      fontSize: 8,
      color: theme.colors.white,
      h: 0.5,
      w: 2.08,
      x: 8.64,
      y: 3.85 + DRIVER_OFFSET * options.index,
    },
  },
  {
    text: options.metricName.toUpperCase(),
    options: {
      fontSize: 10,
      color: theme.colors.white,
      bold: true,
      align: 'center',
      h: 0.27,
      w: 2.03,
      x: 10.72,
      y: 3.15 + DRIVER_OFFSET * options.index,
    },
  },
  {
    text: 'Exposed'.toUpperCase(),
    options: {
      fontSize: 7,
      color: theme.colors.inmarketGreen,
      bold: true,
      h: 0.22,
      w: 0.9,
      x: 10.79,
      y: 3.55 + DRIVER_OFFSET * options.index,
    },
  },
  ...(options.exposedText
    ? [
        {
          text: options.exposedText,
          options: {
            fontSize: 18,
            color: theme.colors.white,
            h: 0.4,
            w: 1.26,
            x: 10.77,
            y: 3.82 + DRIVER_OFFSET * options.index,
          },
        },
      ]
    : []),
  {
    text: 'Control'.toUpperCase(),
    options: {
      fontSize: 7,
      color: theme.colors.inmarketGreen,
      bold: true,
      h: 0.22,
      w: 0.9,
      x: 11.95,
      y: 3.55 + DRIVER_OFFSET * options.index,
    },
  },
  ...(options.controlText
    ? [
        {
          text: options.controlText,
          options: {
            fontSize: 18,
            color: theme.colors.white,
            h: 0.4,
            w: 1.26,
            x: 11.93,
            y: 3.82 + DRIVER_OFFSET * options.index,
          },
        },
      ]
    : []),
];

export const buildDriverTextsOptions = (
  report: RslReport
): Omit<DriverTextsOptions, 'index'>[] => {
  const drivers: Omit<DriverTextsOptions, 'index'>[] = [];

  if (report.penetrationLift !== null) {
    drivers.push({
      tagline: parseContent('<b>MORE</b> <lci>PEOPLE</lci>'),
      value: report.penetrationLift,
      descriptionText: parseContent(
        'The <b>lift in % of users</b> who made at least one purchase during the measurement period.'
      ),
      metricName: 'Penetration Rate',
      exposedText: formatters.percentage(report.penetration, { decimals: 2 }),
      controlText: formatters.percentage(report.controlPenetration, {
        decimals: 2,
      }),
    });
  }

  if (report.transactionSizeLift !== null) {
    drivers.push({
      tagline: parseContent('<b>MORE</b> <lci>PURCHASES</lci>'),
      value: report.transactionSizeLift,
      descriptionText: parseContent(
        'The <b>lift in average dollars per purchase</b> during the measurement period.'
      ),
      metricName: 'Transaction Size',
      exposedText: formatters.currency(report.transactionSize, { decimals: 2 }),
      controlText: formatters.currency(report.controlTransactionSize, {
        decimals: 2,
      }),
    });
  }

  if (report.transactionRateLift !== null) {
    drivers.push({
      tagline: parseContent('<b>MORE</b> <lci>OFTEN</lci>'),
      value: report.transactionRateLift,
      descriptionText: parseContent(
        'The <b>lift in average number of transactions</b> per shopper during the measurement period.'
      ),
      metricName: 'Transaction Rate',
      exposedText: formatters.number(report.transactionRate, { decimals: 2 }),
      controlText: formatters.number(report.controlTransactionRate, {
        decimals: 2,
      }),
    });
  }

  return drivers;
};
